import {DestroyRef, Injectable} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LookupService} from '../../../common/service/lookup.service';
import {AnnotationUtilityService} from './annotation-utility.service';
import {LineTypeLookup} from '../../../common/models/line-type.model';
import {LegalParcelTypeLookup} from '../../../common/models/legal-parcel-type.model';
import {FeatureApplyUpdateService} from '../feature-apply-update.service';
import {SpatialUtilService} from '../spatial-util.service';
import {catchError, map, Observable, of, ReplaySubject, tap} from 'rxjs';
import {AlertModalService} from '../../../common/component/modal';
import {AppConfigService} from '../../../common/service/app-config.service';
import {SpatialSequenceService} from '../../../common/service/spatial-sequence.service';
import {LabelExpressionService} from '../label-expression/label-expression.service';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {AnnotationConfigLookup} from '../../../common/models/annotation-config.model';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {LineService} from './line.service';
import {FeatureOffsetSettingsLookup} from '../../../common/models/feature-offset-settings.model';
import {AngleUtilService} from './angle/angle-util.service';
import {TenureService} from '../../../plan-information/tenure/tenure.service';
import {FeatureAssociationService} from './feature-association.service';
import {switchMap} from 'rxjs/operators';
import {forkJoinDefaultIfEmpty} from '../../../common/observable.utils';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class AnnotationService {
    title = 'AnnotationService';

    private readonly ERROR_NO_LINE_SELECTED =
        'The annotations could not be aligned. You have not chosen a line feature to align the annotation(s) to.';

    private readonly ERROR_MULTIPLE_LINES_SELECTED =
        'The annotations could not be aligned. Annotation can only be aligned to one line selection at a time.';

    private readonly ERROR_NO_ANNOTATIONS_SELECTED = 'You have not chosen any annotations to be aligned.';

    annotationsToBeCreated: any[] = [];

    annotationsToBeUpdated: any[] = [];

    allAnnotations: any[] = [];

    designProperties = this.appConfigService.getDesignProperties();

    createTimeout: number;

    updateTimeout: number;

    labelPointsTimeout: number;

    labelPointsTimeoutInfoList = [];

    constructor(
        private alertModalService: AlertModalService,
        private appConfigService: AppConfigService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private angleUtilService: AngleUtilService,
        private annotationUtilityService: AnnotationUtilityService,
        private esriModuleService: EsriModuleService,
        private featureAssociationService: FeatureAssociationService,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private labelExpressionService: LabelExpressionService,
        private lineService: LineService,
        private lookupService: LookupService,
        private tenureService: TenureService,
        private planModelService: PlanModelService,
        private printAreaService: PrintAreaService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {
        this.eventBus
            .on('createInterestAnnotation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on createInterestAnnotation`);
                const {feature, featureType, polygons, polylines, allAnnotations} = meta.data;
                this.createBusinessTypeAnnotation(feature, featureType, polygons, polylines, allAnnotations);
            });

        this.eventBus
            .on('createRoadAnnotation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on createRoadAnnotation`);
                const {feature, featureType, polygons, polylines, allAnnotations} = meta.data;
                this.createBusinessTypeAnnotation(feature, featureType, polygons, polylines, allAnnotations);
            });

        this.eventBus
            .on('createAnnotation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on createAnnotation`);

                const {feature, featureType, polygons} = meta.data;
                if (feature.attributes.polygonids) {
                    this.create(feature, featureType, polygons || this.getFeaturePolygons(feature), undefined);
                } else {
                    this.create(feature, featureType, undefined, undefined);
                }
            });

        this.eventBus
            .on('updateAnnotation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .pipe(
                switchMap((meta: MetaData) => {
                    console.debug(`${this.title} - on updateAnnotation`);
                    return this.updateAnnotation(meta.data.feature, meta.data.featureType, {
                        updateGeometry: !!meta.data.updateGeometry,
                        createAnnotation: !!meta.data.createAnnotation
                    });
                })
            )
            .subscribe();

        this.eventBus
            .on('deleteAnnotations')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on deleteAnnotations`);

                const {featureLayer, features, parentFeatureType, parentFeatureId} = meta.data;

                this.delete(featureLayer, features, parentFeatureType, parentFeatureId);
            });

        this.eventBus
            .on('createAnnotations')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((meta: MetaData) => {
                    console.debug(`${this.title} - on createAnnotations`);

                    this.createAnnotationsAfterTimeout();

                    const annotations = meta.data;

                    this.populateLabelExpression(annotations, undefined);

                    return this.createAnnotations(annotations, true);
                })
            )
            .subscribe();

        this.eventBus
            .on('updateAnnotations')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .pipe(
                switchMap((meta: MetaData) => {
                    console.debug(`${this.title} - on updateAnnotations`);
                    return this.updateAnnotations(meta.data);
                })
            )
            .subscribe();
    }

    getAnnotationConfigurations(featureType: string, featureSubtype: string | number): AnnotationConfigLookup[] {
        console.debug(`${this.title} - getAnnotationConfigurations`);

        return this.lookupService
            .getLookups('annotationConfig')
            .filter((ac: AnnotationConfigLookup) => ac.featureType === featureType && ac.featureSubTypes?.split(',').includes(`${featureSubtype}`));
    }

    getTotalLotBoundaryLineTypeLookup() {
        return this.lookupService.getLookups('lineType').find((l: LineTypeLookup) => l.businessClass === 'TotalLotBoundary');
    }

    createAnnotations(annotations: any[], generalAnnotationGrouping: boolean) {
        const logPrefix = `${this.title} - createAnnotations`;

        console.debug(logPrefix);

        if (annotations.length === 0) {
            return of(null);
        }

        const sequence = this.spatialSequenceService.getAnnotationSequence(annotations.length);

        annotations.forEach((annotation, index) => {
            annotation.attributes.id = sequence[index];

            // If greater than 1 it is a group association
            if (generalAnnotationGrouping && annotations.length > 1) {
                annotation.attributes.featureid = annotations[0].attributes.id;
            }
        });

        return this.spatialUtilService.applyEdits('annotation', annotations, null, null).pipe(
            map(res => {
                console.debug(`${logPrefix} - adds:`, res.adds);

                if (res.adds[0].success) {
                    console.debug(`${logPrefix} - annotations created successfully`);

                    this.eventBus.cast(this.designProperties.EVENTS.FEATURES_CREATED, {
                        features: annotations,
                        featureLayer: this.spatialUtilService.getAnnotationMapLayer()
                    });

                    return true;
                } else {
                    throw new Error(res.adds[0].error?.message);
                }
            }),
            catchError(err => {
                let errorMessage = 'Error creating annotation features.';
                if (err) {
                    errorMessage = `${errorMessage} Error : ${err}`;
                }
                console.error(`${logPrefix}`, errorMessage);
                this.alertModalService.createAlertMessage(errorMessage, 'Ok');
                return err;
            })
        );
    }

    createBusinessTypeAnnotation(feature: any, featureType: string, polygons: any[], polylines: any[], allAnnotations: any[]) {
        this.createAnnotationsAfterTimeout();
        this.allAnnotations = allAnnotations;

        if (feature.attributes.polygonids === null) {
            this.create(feature, featureType, undefined, polylines);
        } else {
            if (!polygons) {
                polygons = this.getFeaturePolygons(feature);
            }
            this.create(feature, featureType, polygons, polylines);
        }
    }

    updateAnnotation(feature: any, featureType: string, options?: {updateGeometry?: boolean; createAnnotation?: boolean}) {
        console.debug(`${this.title} - updateAnnotation`);

        const updateGeometry = !!options?.updateGeometry;
        const createAnnotation = !!options?.createAnnotation;

        if (feature.attributes.polygonids) {
            //Update area annotation for business feature (lot, road) when area is updated through edit tool
            const polygons = this.spatialUtilService
                .getPolygons()
                .filter((p: any) => feature.attributes.polygonids && feature.attributes.polygonids.includes(`[${p.attributes.id}]`));

            feature.attributes['polygon.statedarea'] = feature.attributes.statedarea;

            return this.update(feature, featureType, {
                polygons: polygons,
                updateGeometry: updateGeometry,
                createAnnotation: createAnnotation
            });
        } else if (featureType === 'polygon' && (feature.attributes.lotid || feature.attributes.roadid)) {
            if (
                ![10, 11].includes(feature.attributes.type) ||
                this.lookupService
                    .getLookups('legalParcelType')
                    .some(l => ['Road', 'Road Widening'].includes(l.description) && l.id === feature.attributes.type)
            ) {
                const parentFeatureType = feature.attributes.lotid ? 'lot' : 'road';
                const parentFeatureId = feature.attributes.lotid || feature.attributes.roadid;
                const parentFeature = this.spatialUtilService.getGraphics(parentFeatureType).find((f: any) => f.attributes.id === parentFeatureId);

                feature.attributes['polygon.statedarea'] = feature.attributes.statedarea;
                parentFeature.attributes['polygon.statedarea'] = feature.attributes.statedarea;

                const polygons = this.spatialUtilService
                    .getPolygons()
                    .filter((p: any) => parentFeature.attributes.polygonids.includes(`[${p.attributes.id}]`));

                this.featureAssociationService.setTotalAreaAndGeometry(parentFeature, polygons);

                return this.update(parentFeature, parentFeatureType, {
                    polygons: polygons,
                    updateGeometry: updateGeometry,
                    childPolygon: feature,
                    createAnnotation: createAnnotation
                }).pipe(switchMap(() => this.spatialUtilService.applyEdits(parentFeatureType, null, [parentFeature], null)));
            }
            return of(null);
        } else {
            return this.update(feature, featureType, {updateGeometry: updateGeometry, createAnnotation: createAnnotation});
        }
    }

    updateAnnotations(annotations: any[]) {
        console.debug(`${this.title} - updateAnnotations`);

        this.updateAnnotationsAfterTimeout();

        const modifiedAnnotations = [];

        annotations.forEach((a: any) => {
            const graphicToModify = new this.esriModuleService.Graphic(a.toJson());

            if ([2, 24, 25].includes(graphicToModify.attributes.featuresubtype)) {
                const count = graphicToModify.attributes.attribute1.split('[').length;
                let attribute1 = graphicToModify.attributes.attribute1;

                if (graphicToModify.attributes.attribute2) {
                    attribute1 = attribute1.replace(/\[(.+?)]/, graphicToModify.attributes.attribute2);
                }
                attribute1 = attribute1.replace(/( METRES )/, '$1 \n');

                if (count > 2) {
                    if (graphicToModify.attributes.attribute3) {
                        if (graphicToModify.attributes.featuresubtype === 24) {
                            graphicToModify.attributes.attribute3 = this.tenureService.sanitiseRangesText(graphicToModify.attributes.attribute3);
                        }
                        attribute1 = attribute1.replace(/\[(.+?)]/, graphicToModify.attributes.attribute3);
                    } else {
                        //TODO: Can be done in graphicToModify generic way
                        if (graphicToModify.attributes.featuresubtype === 25) {
                            const expr = graphicToModify.attributes.labelexpression.match('AS TO(.*)ONLY');
                            if (expr.length === 2) {
                                attribute1 = attribute1.replace(/\[(.+?)]/, expr[1]);
                            }
                        }
                    }
                }
                graphicToModify.attributes['attributeCustom'] = attribute1;
            }
            modifiedAnnotations.push(graphicToModify);
        });

        this.populateLabelExpression(modifiedAnnotations, undefined);

        return this.commitAnnotationsUpdate(modifiedAnnotations);
    }

    deleteAnnotations(annotations: any[]) {
        console.debug(`${this.title} - deleteAnnotations`);

        const annotationsToDelete = this.spatialUtilService.toApplyEditsDeletes('annotation', annotations);

        if (annotationsToDelete.length === 0) {
            return of(null);
        }

        return this.spatialUtilService.applyEdits('annotation', null, null, annotationsToDelete).pipe(
            tap((res: any) => {
                const d0 = res.deletes && res.deletes[0];
                if (d0 && d0.success) {
                    console.debug(`${this.title} - deleteAnnotations - annotations deleted successfully`);
                } else if (d0 && !d0.success) {
                    this.alertModalService.createAlertMessage(
                        `Error deleting annotation features: ${(d0 && d0.error && d0.error.message) || ''}`,
                        'Ok'
                    );
                }
            }),
            catchError(err => this.alertModalService.createAlertMessage(`Error deleting annotation features: ${err}`, 'Ok'))
        );
    }

    populateLabelExpressionAndCreateAnnotations(annotations: any[]) {
        console.debug(`${this.title} - populateLabelExpressionAndCreateAnnotations`);

        this.populateLabelExpression(annotations, undefined);
        return this.createAnnotations(annotations, false);
    }

    getPathLengthInfo(path: any[]) {
        const pathLengthInfo = path
            .map(v => new this.esriModuleService.Point(v[0], v[1], this.planModelService.getSpatialReference()))
            .reduce(
                (acc, v, i, a) => {
                    const lastSegmentLength = i > 0 ? this.esriModuleService.getEsri().geometry.getLength(a[i - 1], v) : 0.0;
                    const cumulativeLength = acc.totalLength + lastSegmentLength;
                    acc.cumulativeLengths.push(cumulativeLength);
                    acc.totalLength = cumulativeLength;
                    return acc;
                },
                {totalLength: 0, cumulativeLengths: []}
            );

        pathLengthInfo.halfLength = pathLengthInfo.totalLength / 2.0;
        pathLengthInfo.middleSegmentIndex = pathLengthInfo.cumulativeLengths.findIndex(
            (v: any, i: number, a: any[]) => i < a.length - 1 && a[i] < pathLengthInfo.halfLength && a[i + 1] > pathLengthInfo.halfLength
        );

        return pathLengthInfo;
    }

    align() {
        const selectedLineFeatures = this.spatialUtilService.getHighlightedLines();
        const selectedAnnotationFeatures = this.spatialUtilService.getHighlightedAnnotations();
        const selectedAngleFeatures = this.spatialUtilService.getHighlightedAngles();

        let errMsg: string;

        if (selectedLineFeatures.length === 0) {
            errMsg = this.ERROR_NO_LINE_SELECTED;
        } else if (selectedLineFeatures.length > 1) {
            errMsg = this.ERROR_MULTIPLE_LINES_SELECTED;
        } else if (selectedAnnotationFeatures.length === 0 && selectedAngleFeatures.length === 0) {
            errMsg = this.ERROR_NO_ANNOTATIONS_SELECTED;
        }

        if (errMsg) {
            console.error(errMsg);
            this.alertModalService.createAlertMessage(errMsg, 'OK', 'Error', 'error');
            return;
        }

        const selectedLineFeature = selectedLineFeatures[0];

        const bearing = this.angleUtilService.getLineBearing(selectedLineFeature);
        const bearingDms = this.angleUtilService.ddToDms(bearing);

        selectedAnnotationFeatures.forEach((annotationFeature: any) => {
            annotationFeature.attributes.rotationdms = bearingDms;
            annotationFeature.attributes.rotation = bearing;
        });

        selectedAngleFeatures.forEach((angleFeature: any) => {
            angleFeature.attributes.anglerotationdms = bearingDms;
            angleFeature.attributes.anglerotation = bearing;
        });

        const observables: Observable<any>[] = [];

        if (selectedAnnotationFeatures.length) {
            const annotationMapLayer = this.spatialUtilService.getAnnotationMapLayer();
            let observable = this.updateFeatures(annotationMapLayer, selectedAnnotationFeatures);
            if (this.spatialUtilService.getMapMode().type === 'mainPlan') {
                observable = observable.pipe(
                    map(() => {
                        const parentIds = selectedAnnotationFeatures.map((a: any) => a.attributes.id);
                        return this.spatialUtilService
                            .getAnnotationGraphics()
                            .filter((f: any) => f.attributes.enlargementid && parentIds.includes(f.attributes.parentid));
                    }),
                    switchMap(enlargementAnnotationFeatures => {
                        enlargementAnnotationFeatures.forEach(annotationFeature => {
                            annotationFeature.attributes.rotationdms = bearingDms;
                            annotationFeature.attributes.rotation = bearing;
                        });
                        return this.updateFeatures(this.spatialUtilService.getFeatureLayer('annotation'), enlargementAnnotationFeatures);
                    })
                );
            }
            observables.push(observable);
        }

        if (selectedAngleFeatures.length) {
            const angleMapLayer = this.spatialUtilService.getAngleMapLayer();
            let observable = this.updateFeatures(angleMapLayer, selectedAngleFeatures);
            if (this.spatialUtilService.getMapMode().type === 'mainPlan') {
                observable = observable.pipe(
                    map(() => {
                        const parentIds = selectedAngleFeatures.map((a: any) => a.attributes.id);
                        return this.spatialUtilService
                            .getAngleGraphics()
                            .filter((f: any) => f.attributes.enlargementid && parentIds.includes(f.attributes.parentid));
                    }),
                    switchMap(enlargementAngleFeatures => {
                        enlargementAngleFeatures.forEach(angleFeature => {
                            angleFeature.attributes.anglerotationdms = bearingDms;
                            angleFeature.attributes.anglerotation = bearing;
                        });
                        return this.updateFeatures(this.spatialUtilService.getFeatureLayer('angle'), enlargementAngleFeatures);
                    })
                );
            }
            observables.push(observable);
        }

        forkJoinDefaultIfEmpty(observables).subscribe(() => this.spatialUtilService.unhighlightAllFeatures());
    }

    flipAnnotations() {
        const logPrefix = `${this.title} - flipAnnotations`;
        console.debug(logPrefix);

        const selectedAnnotations = this.spatialUtilService
            .getHighlightedAnnotations()
            .map((a: any) => new this.esriModuleService.Graphic(a.toJson()));
        const selectedLines = this.spatialUtilService.getHighlightedLines().filter((l: any) => l.geometry && l.attributes && !l.attributes.pointids);
        const selectedPolylines = this.spatialUtilService.getHighlightedPolylines();

        const flippedLineAnnotations = [];

        const moveGraphics = this.spatialUtilService
            .getMapGraphicsArray()
            .filter(graphic => !graphic.attributes && graphic.geometry.type === 'point');

        selectedAnnotations.forEach((selectedAnnotation: any) => {
            let line: any;
            if (selectedAnnotation.attributes.featuretype === 'line') {
                line = this.spatialUtilService.getLineGraphics().find((g: any) => g.attributes.id === selectedAnnotation.attributes.featureid);
            } else if (selectedAnnotation.attributes.featuretype === 'constructLine') {
                line = this.spatialUtilService
                    .getConstructLineGraphics()
                    .find((g: any) => g.attributes.id === selectedAnnotation.attributes.featureid);
            }
            if (line?.geometry && !line.attributes.pointids) {
                const matchedGraphics = moveGraphics.filter(
                    (moveGraphic: any) =>
                        moveGraphic.geometry.x === selectedAnnotation.geometry.x && moveGraphic.geometry.y === selectedAnnotation.geometry.y
                );

                const flippedLineAnnotation = this.flipLineAnnotation(
                    selectedAnnotation,
                    line.geometry.paths[0][0][0],
                    line.geometry.paths[0][0][1],
                    line.geometry.paths[0][1][0],
                    line.geometry.paths[0][1][1]
                );

                if (flippedLineAnnotation) {
                    // update the position of move graphics to match the flipped annotation
                    matchedGraphics.forEach((matchedGraphic: any) => {
                        matchedGraphic.geometry.x = flippedLineAnnotation.geometry.x;
                        matchedGraphic.geometry.y = flippedLineAnnotation.geometry.y;
                    });
                    flippedLineAnnotations.push(flippedLineAnnotation);
                }
            }

            this.flip(selectedAnnotation, moveGraphics, flippedLineAnnotations);
        });

        selectedLines.forEach((line: any) =>
            this.spatialUtilService
                .getAnnotationGraphics()
                .filter(
                    (g: any) =>
                        g?.attributes.featuretype &&
                        ['line', 'polyline'].includes(g.attributes.featuretype) &&
                        g.attributes.featureid === line.attributes.id &&
                        !selectedAnnotations.some((a: any) => a.attributes.id === g.attributes.id)
                )
                .forEach((annotation: any) => {
                    const flippedLineAnnotation = this.flipLineAnnotation(
                        annotation,
                        line.geometry.paths[0][0][0],
                        line.geometry.paths[0][0][1],
                        line.geometry.paths[0][1][0],
                        line.geometry.paths[0][1][1]
                    );
                    if (flippedLineAnnotation) {
                        flippedLineAnnotations.push(flippedLineAnnotation);
                    }
                })
        );

        selectedPolylines.forEach((line: any) =>
            this.spatialUtilService
                .getAnnotationGraphics()
                .filter(
                    (g: any) =>
                        g?.attributes.childtype &&
                        ['line', 'polyline'].includes(g.attributes.childtype) &&
                        g.attributes.childid === line.attributes.id
                )
                .forEach((annotation: any) => this.flip(annotation, moveGraphics, flippedLineAnnotations))
        );

        this.reversePolylines(selectedPolylines);

        if (selectedPolylines.length) {
            this.featuresApplyUpdateService
                .updateFeaturesByType(this.spatialUtilService.getLineMapLayer(), selectedPolylines)
                .subscribe(updatedFeatures => {
                    if (updatedFeatures) {
                        console.debug(`${logPrefix} - polyline features flipped`);
                    } else {
                        console.debug(`${logPrefix} - Error flipping polyline features`);
                    }
                });
        }

        if (flippedLineAnnotations.length) {
            this.featuresApplyUpdateService
                .updateFeaturesByType(this.spatialUtilService.getAnnotationMapLayer(), flippedLineAnnotations)
                .subscribe(updatedFeatures => {
                    if (updatedFeatures) {
                        console.debug(`${logPrefix} - annotation features flipped`);
                    } else {
                        console.debug(`${logPrefix} - Error flipping annotation features`);
                    }
                });
        }
    }

    private flip(selectedAnnotation: any, moveGraphics: any[], flippedLineAnnotations: any[]) {
        if (selectedAnnotation.attributes.featuretype === 'interest' && selectedAnnotation.attributes.childtype === 'polyline') {
            const polyline = this.spatialUtilService
                .getPolylineGraphics()
                .find((p: any) => p.attributes.id === selectedAnnotation.attributes.childid);
            if (polyline && polyline.geometry) {
                const graphicsToMove = moveGraphics.filter(
                    g => g.geometry.x === selectedAnnotation.geometry.x && g.geometry.y === selectedAnnotation.geometry.y
                );

                const mp = this.calcPolylineMidpoint(polyline.geometry, selectedAnnotation);
                const flippedLineAnnotation = this.flipLineAnnotation(selectedAnnotation, mp.x0, mp.y0, mp.x1, mp.y1);
                if (flippedLineAnnotation) {
                    flippedLineAnnotations.push(flippedLineAnnotation);

                    graphicsToMove.forEach(g => {
                        g.geometry.x = flippedLineAnnotation.geometry.x;
                        g.geometry.y = flippedLineAnnotation.geometry.y;
                    });
                }
            }
        }
    }

    private getPolylineLength(polylineGeometry: any) {
        return this.getPathLengthInfo(polylineGeometry.paths[0]).totalLength;
    }

    private calcPolylineMidpoint(polylineGeometry: any, annotation: any) {
        const halfDistance = this.getPolylineLength(polylineGeometry) / 2.0;

        let currentLength = 0;
        let fromPoint: any;
        let toPoint: any;

        for (let i = 0; i < polylineGeometry.paths[0].length; i++) {
            const coords = polylineGeometry.paths[0][i];
            fromPoint = new this.esriModuleService.Point(coords[0], coords[1], this.planModelService.getSpatialReference());
            if (toPoint) {
                const segLength = this.esriModuleService.getEsri().geometry.getLength(fromPoint, toPoint);
                currentLength += segLength;
                if (currentLength > halfDistance) {
                    // we have found the two points exceeding the traveled distance now find the midpoint between them
                    let distanceToTravel = segLength / 2.0;
                    const bearing = this.lineService.calculateBearing(fromPoint.x, fromPoint.y, toPoint.x, toPoint.y);

                    let midpoint = this.lineService.getPointOffsetByBearingAndDistance(bearing, distanceToTravel);
                    midpoint.x = fromPoint.x + midpoint.x;
                    midpoint.y = fromPoint.y + midpoint.y;

                    for (let j = 0; j < this.allAnnotations.length; j++) {
                        const a = this.allAnnotations[j];
                        if (a.attributes.id === annotation.attributes.id || !a.geometry) {
                            continue;
                        }

                        const dist = Math.sqrt(
                            (a.geometry.y - midpoint.y) * (a.geometry.y - midpoint.y) + (a.geometry.x - midpoint.x) * (a.geometry.x - midpoint.x)
                        );

                        if (dist < 2.0) {
                            //points are too close to each other
                            j = -1;
                            distanceToTravel = distanceToTravel + 2;
                            midpoint = this.lineService.getPointOffsetByBearingAndDistance(bearing, distanceToTravel);
                            midpoint.x = fromPoint.x + midpoint.x;
                            midpoint.y = fromPoint.y + midpoint.y;
                        }
                    }

                    return {
                        bearing: bearing,
                        x: midpoint.x,
                        y: midpoint.y,
                        x0: fromPoint.x,
                        y0: fromPoint.y,
                        x1: toPoint.x,
                        y1: toPoint.y
                    };
                }
            }
            toPoint = fromPoint;
        }

        return null;
    }

    private populateLabelExpression(features: any[], polygons: any[]) {
        console.debug(`${this.title} - populateLabelExpression`);

        features.forEach(feature => {
            const labelProperties = this.labelExpressionService.create(feature.attributes, polygons);
            if (labelProperties) {
                feature.attributes.labelproperties = JSON.stringify(labelProperties.labels);
                feature.attributes.labelexpression = labelProperties.labelExpression;
            }
        });
    }

    private getAnnotationStartPt(polygonCentroid: any, polygon: any, polygonOffset: any) {
        // get the annotations for this polygon
        const annotations = this.allAnnotations.filter(a => a.attributes.childtype === 'polygon' && a.attributes.childid === polygon.attributes.id);

        // if there are no annotations create the label at the centre of the polygon
        if (annotations.length === 0) {
            return [polygonCentroid.x, polygonCentroid.y];
        }

        // get the label point for the annotation based on all the annotations in this polygon
        let xToTest = polygonCentroid.x;
        let yToTest = polygonCentroid.y;
        let counter = 1;
        let conflict = false;
        let offsetDist = this.printAreaService.getReferenceScaleDivisor() * polygonOffset;
        const offsetIncrement = offsetDist;

        // loop through other annotations in this polygon
        while (counter < 10) {
            conflict = false;
            // loop though all annotations for this polygon and check if annotation point is within 1m of it
            for (let i = 0; i < annotations.length; i++) {
                let conflictY = false;
                let conflictX = false;

                const xDiff = Math.abs(xToTest - annotations[i].geometry.x);
                const yDiff = Math.abs(yToTest - annotations[i].geometry.y);

                // Check if x and y coords are within 0.01 of each other
                if (yDiff >= 0 && yDiff <= 0.01) {
                    if (xDiff >= 0 && xDiff <= 0.01) {
                        conflictX = true;
                        conflictY = true;
                    }
                }

                if (xDiff < offsetIncrement + 0.01) {
                    if (xDiff >= 0.01 && xDiff <= offsetIncrement - 0.1) {
                        // Conflict in x Coordinate
                        conflictX = true;
                    }
                }

                //Check for conflict in Y Coords
                if (yDiff < offsetIncrement + 0.01) {
                    if (yDiff >= 0.01 && yDiff <= offsetIncrement - 0.1) {
                        // conflict in y coords
                        conflictY = true;
                    }
                }

                if (conflictX || conflictY) {
                    conflict = true;
                    break;
                }
            }

            if (conflict) {
                offsetDist = offsetDist + offsetIncrement;
                xToTest = polygonCentroid.x;
                yToTest = polygonCentroid.y + offsetDist;
            } else {
                xToTest = polygonCentroid.x;
                yToTest = polygonCentroid.y + offsetDist;
                break;
            }

            counter++;
        }

        // assign the annotation label coordinates if there is no conflict
        if (conflict === false) {
            return [xToTest, yToTest];
        }

        return null;
    }

    private getFeaturePolygons(feature: any) {
        if (!feature.attributes) {
            return [];
        }
        return this.spatialUtilService.getPolygonGraphics().filter((p: any) => feature.attributes.polygonids.includes(`[${p.attributes.id}]`));
    }

    private getReferenceScaleDivisor() {
        return this.printAreaService.getReferenceScaleDivisor();
    }

    private findAttributeNumberCount(attributes: any) {
        let count = 0;
        for (let i = 1; i <= 8; i += 1) {
            //attribute9 and attribute10 are reserved for annotationsets
            if (attributes[`attribute${i}`]) {
                count++;
            }
        }
        return count;
    }

    private setOffset(annotation: any, x: number, y: number, spatialReference: any) {
        annotation.geometry = new this.esriModuleService.Point(x, y);
        annotation.geometry.setSpatialReference(spatialReference);
    }

    private applyDefaultOffset(annotation: any, centroid: any, offsetX: number, spatialReference: any, featureType: string, featureSubType: string) {
        const baseOffset = this.getReferenceScaleDivisor() * 0.004;
        let offsetY = 0;
        let centroidOffset = 0;

        const annotationConfiguration = this.getAnnotationConfigurations(featureType, featureSubType).find(
            (ac: AnnotationConfigLookup) => ![null, undefined].includes(annotation.attributes[ac.annotationAttribute])
        );
        if (annotationConfiguration) {
            const attributeNumber = annotationConfiguration.annotationAttribute.replace(/^\D+/g, '');
            centroidOffset = annotationConfiguration.centroidOffset ? annotationConfiguration.centroidOffset * this.getReferenceScaleDivisor() : 0;
            offsetY = baseOffset * (parseInt(attributeNumber) - 1);
        }
        this.setOffset(annotation, centroid.x + offsetX, centroid.y - offsetY + centroidOffset, spatialReference);
    }

    private setAnnotationsGeometry(feature: any, featureType: string, polygons: any[], polylines: any[], annotations: any[]) {
        const logPrefix = `${this.title} - setAnnotationsGeometry`;

        console.debug(logPrefix);
        if (!feature.geometry) {
            return of(null);
        }

        let geometryType = feature.geometry.type === 'polyline' ? 'line' : feature.geometry.type;

        const offsetLookup = this.lookupService
            .getLookups('featureOffsetSettings')
            .find((lookup: FeatureOffsetSettingsLookup) => lookup.validCoordSystemIds.includes(this.planModelService.getCoordinateSystem().id));

        const totalLotBoundaryLineTypeLookup = this.getTotalLotBoundaryLineTypeLookup();

        return forkJoinDefaultIfEmpty(
            annotations.map((annotation: any) => {
                const replaySubject = new ReplaySubject(1);
                replaySubject.next(null);

                if (annotation.attributes.childtype) {
                    geometryType = annotation.attributes.childtype;
                }

                const offsetMultiplier = annotation.configuration.oppositeLocation ? -1 : 1;

                let centroidOffset = annotation.configuration.centroidOffset
                    ? annotation.configuration.centroidOffset * this.getReferenceScaleDivisor()
                    : 0;

                if (annotation.attributes.rotation) {
                    centroidOffset = annotation.attributes.rotation > 180 ? centroidOffset * -1 : centroidOffset;
                }

                const polygonId = this.getHighestAreaPolygonId(polygons);

                const roadLegalParcelTypeLookup = this.getRoadLegalParcelTypeLookup();
                const roadWideningLegalParcelTypeLookup = this.getRoadWideningLegalParcelTypeLookup();

                const validRoadType = roadLegalParcelTypeLookup.id === annotation.attributes.featuresubtype;

                switch (geometryType) {
                    case 'point':
                        if (!(annotation.attributes && annotation.attributes.enlargementid)) {
                            const offsetX = feature.geometry.x + this.getReferenceScaleDivisor() * offsetMultiplier * offsetLookup.pointOffset;
                            const offsetY = feature.geometry.y;

                            if (
                                !annotation.geometry ||
                                (Math.abs(offsetY - annotation.geometry.y) <= 0.1 && Math.abs(offsetX - annotation.geometry.x) <= 0.1)
                            ) {
                                this.setOffset(annotation, feature.geometry.x, feature.geometry.y, feature.geometry.spatialReference);
                                const offsetDist = this.getReferenceScaleDivisor() * offsetMultiplier * offsetLookup.pointOffset;
                                annotation.attributes.x = annotation.geometry.x = parseFloat(annotation.geometry.x) + offsetDist;
                            }
                        }
                        replaySubject.complete();
                        break;
                    case 'line':
                        if (totalLotBoundaryLineTypeLookup) {
                            const lineTypeId = totalLotBoundaryLineTypeLookup.id;
                            feature.geometry.clearCache();

                            const center = feature.geometry.getExtent().getCenter();

                            if (lineTypeId === parseInt(feature.attributes.readValuetype || feature.attributes.type)) {
                                this.setOffset(annotation, center.x, center.y - centroidOffset, feature.geometry.spatialReference);
                                const offsetDist = this.getReferenceScaleDivisor() * offsetMultiplier * offsetLookup.boundaryLineOffset;
                                this.offsetGeometry(offsetDist, annotation);
                            } else {
                                this.setOffset(annotation, center.x, center.y - centroidOffset, feature.geometry.spatialReference);

                                let offsetDist: number;

                                if (annotation.attributes && annotation.attributes.enlargementid) {
                                    const enlargement = this.spatialUtilService
                                        .getEnlargements()
                                        .find((f: any) => f.attributes.id === annotation.attributes.enlargementid);
                                    offsetDist = enlargement.attributes.scale * offsetMultiplier * offsetLookup.lineOffset;
                                } else {
                                    offsetDist = this.getReferenceScaleDivisor() * offsetMultiplier * offsetLookup.pointOffset;
                                }

                                this.offsetGeometry(offsetDist, annotation);
                            }
                        }
                        replaySubject.complete();
                        break;
                    case 'polyline':
                        if (polylines) {
                            if (featureType === 'interest') {
                                const polyline = polylines.find(
                                    e => e.attributes.id === annotation.attributes.childid && annotation.attributes.childtype === 'polyline'
                                );

                                const midpoint = this.calcPolylineMidpoint(polyline.geometry, annotation);
                                if (midpoint) {
                                    if (annotation.attributes) {
                                        annotation.attributes.rotation = midpoint.bearing;
                                        annotation.attributes.rotationdms = this.angleUtilService.ddToDms(midpoint.bearing);
                                    }
                                    this.setOffset(annotation, midpoint.x, midpoint.y, feature.geometry.spatialReference);
                                    const offsetDist = this.getReferenceScaleDivisor() * offsetMultiplier * offsetLookup.lineOffset;
                                    this.offsetGeometry(offsetDist, annotation);
                                }
                            }
                        }
                        replaySubject.complete();
                        break;
                    case 'polygon':
                        if (polygons) {
                            let polygon = polygons.find(
                                e => e.attributes.id === annotation.attributes.childid && annotation.attributes.childtype === 'polygon'
                            );

                            switch (featureType) {
                                case 'lot':
                                    if (!polygon) {
                                        replaySubject.complete();
                                        break;
                                    }

                                    if (annotations.length > 0) {
                                        this.getPolygonCentroid(polygon, (centroid: any) => {
                                            let loopThrough = true;
                                            const attributeNumberCount = this.findAttributeNumberCount(annotation.attributes);
                                            const featureSubType = String(this.getRealValue(featureType, feature, 'type'));

                                            const annotationConfigurations = this.getAnnotationConfigurations(featureType, featureSubType);
                                            if (annotationConfigurations.length === 0) {
                                                replaySubject.complete();
                                                return;
                                            }

                                            const offsetX = this.calculateOffsetX(polygon, annotationConfigurations);

                                            annotationConfigurations.forEach(ac => {
                                                if (![null, undefined].includes(annotation.attributes[ac.annotationAttribute]) && loopThrough) {
                                                    if (ac.singleAnnoOffsetDistance) {
                                                        const offsetY =
                                                            this.getReferenceScaleDivisor() *
                                                            ((ac.centroidOffset || 0) - ac.singleAnnoOffsetDistance);
                                                        this.setOffset(
                                                            annotation,
                                                            centroid.x + offsetX,
                                                            centroid.y + offsetY,
                                                            polygon.geometry.spatialReference
                                                        );
                                                    } else {
                                                        this.applyDefaultOffset(
                                                            annotation,
                                                            centroid,
                                                            offsetX,
                                                            polygon.geometry.spatialReference,
                                                            featureType,
                                                            featureSubType
                                                        );
                                                    }

                                                    loopThrough = attributeNumberCount <= 1;
                                                }
                                            });

                                            replaySubject.complete();
                                        });
                                    } else {
                                        this.getPolygonCentroid(polygon, (centroid: any) => {
                                            this.setOffset(annotation, centroid.x, centroid.y, polygon.geometry.spatialReference);
                                            replaySubject.complete();
                                        });
                                    }

                                    break;
                                case 'road':
                                    if (!polygon || validRoadType) {
                                        polygon = polygons.find(p => p.attributes.id === polygonId);
                                    }
                                    if (validRoadType) {
                                        annotation.attributes.childid = polygonId;
                                    }

                                    if (polygon && !annotation.geometry /* Road Annotations Geometry remains during update*/) {
                                        this.getPolygonCentroid(polygon, (centroid: any) => {
                                            const featureSubType = this.determineFeatureSubType(
                                                feature,
                                                polygon,
                                                [roadLegalParcelTypeLookup],
                                                [roadWideningLegalParcelTypeLookup],
                                                featureType
                                            );
                                            annotation.attributes.featuresubtype = featureSubType;

                                            let loopThrough = true;
                                            const attributeNumberCount = this.findAttributeNumberCount(annotation.attributes);

                                            const annotationConfigurations = this.getAnnotationConfigurations(featureType, featureSubType);

                                            if (annotationConfigurations.length === 0) {
                                                replaySubject.complete();
                                                return;
                                            }

                                            const offsetX = this.calculateOffsetX(polygon, annotationConfigurations);

                                            annotationConfigurations.forEach(ac => {
                                                if (![null, undefined].includes(annotation.attributes[ac.annotationAttribute]) && loopThrough) {
                                                    if (ac.singleAnnoOffsetDistance) {
                                                        const offsetY =
                                                            this.getReferenceScaleDivisor() *
                                                            ((ac.centroidOffset || 0) - ac.singleAnnoOffsetDistance);
                                                        this.setOffset(
                                                            annotation,
                                                            centroid.x + offsetX,
                                                            centroid.y + offsetY,
                                                            polygon.geometry.spatialReference
                                                        );
                                                    } else {
                                                        this.applyDefaultOffset(
                                                            annotation,
                                                            centroid,
                                                            offsetX,
                                                            polygon.geometry.spatialReference,
                                                            featureType,
                                                            featureSubType
                                                        );
                                                    }

                                                    loopThrough = attributeNumberCount <= 1;
                                                }
                                            });

                                            replaySubject.complete();
                                        });
                                    } else {
                                        replaySubject.complete();
                                    }
                                    break;
                                case 'interest':
                                    if (polygon) {
                                        this.getPolygonCentroid(polygon, (centroid: any) => {
                                            // get the label point offset based on all the annotations for this polygon
                                            const startXY = this.getAnnotationStartPt(
                                                centroid,
                                                polygon,
                                                offsetMultiplier * offsetLookup.polygonOffset
                                            );
                                            if (startXY) {
                                                this.setOffset(annotation, startXY[0], startXY[1], polygon.geometry.spatialReference);
                                            } else {
                                                console.debug(`${logPrefix} - Unable to determine offset for interest annotation`);
                                                this.setOffset(annotation, centroid.x - 1, centroid.y, polygon.geometry.spatialReference);
                                            }
                                            replaySubject.complete();
                                        });
                                    } else {
                                        replaySubject.complete();
                                    }
                                    break;
                                default:
                                    if (polygon) {
                                        this.getPolygonCentroid(polygon, (centroid: any) => {
                                            this.setOffset(annotation, centroid.x, centroid.y, polygon.geometry.spatialReference);
                                            replaySubject.complete();
                                        });
                                    } else {
                                        replaySubject.complete();
                                    }
                                    break;
                            }
                        } else {
                            replaySubject.complete();
                        }
                        break;
                }

                // remove any configuration node
                delete annotation.configuration;

                return replaySubject;
            })
        );
    }

    private createAnnotation(
        polygon: any,
        roadTypes: any[],
        polygonId: number,
        polyline: any,
        feature: any,
        featureType: string,
        featureSubType: string,
        polygons: any[],
        polylines: any[]
    ) {
        console.debug(`${this.title} - createAnnotation`);

        const annotationSetGuid = this.annotationUtilityService.newAnnotationSetGuid();

        const annotationConfigurations = this.getAnnotationConfigurations(featureType, featureSubType);

        const annotationsToBeCreated = [];

        annotationConfigurations.forEach(annotationConfiguration => {
            if (!polygon && !polyline) {
                this.createAnnotationObject(feature, annotationConfiguration, undefined, annotationsToBeCreated, featureType, undefined, undefined);
                return;
            }

            if (polyline) {
                this.createAnnotationObject(feature, annotationConfiguration, undefined, annotationsToBeCreated, featureType, undefined, polyline);
                if (!polygon) {
                    return;
                }
            }

            let localRoadTypePolygonCount = 0;
            let givenPolygonCount = 0;

            const localPolygonIds = feature.attributes.polygonids.split(',');
            localPolygonIds.forEach((localPolygonId: any) => {
                const localChildId = parseInt(localPolygonId.replace(/\[/g, '').replace(/]/g, ''));
                const localChild = this.spatialUtilService.getPolygonGraphics().find((g: any) => g.attributes.id === localChildId);

                roadTypes.forEach(roadType => {
                    if (localChild.attributes.type === roadType.id) {
                        localRoadTypePolygonCount++;
                    }
                });
            });

            polygons.forEach(givenPolygon => {
                roadTypes.forEach(roadType => {
                    if (givenPolygon.attributes.type === roadType.id) {
                        givenPolygonCount++;
                    }
                });
            });

            roadTypes.forEach(roadType => {
                if (
                    polygon.attributes.type !== roadType.id ||
                    (polygon.attributes.type === roadType.id && polygon.attributes.lotid) ||
                    (polygon.attributes.type === roadType.id &&
                        polygonId === polygon.attributes.id &&
                        localRoadTypePolygonCount === givenPolygonCount)
                ) {
                    if (featureType === 'road') {
                        feature.attributes.type = polygon.attributes.type;
                    }

                    this.createAnnotationObject(
                        feature,
                        annotationConfiguration,
                        polygon,
                        annotationsToBeCreated,
                        featureType,
                        annotationSetGuid,
                        undefined
                    );
                }
            });
        });

        if (annotationsToBeCreated.length > 0) {
            this.setAnnotationsGeometry(feature, featureType, polygons, polylines, annotationsToBeCreated).subscribe(() =>
                this.createAnnotationFeature(feature, polygons, polylines, featureType, annotationsToBeCreated)
            );
        }
    }

    private createAnnotationFeature(feature: any, polygons: any[], polylines: any[], featureType: string, annotationsToBeCreated: any[]) {
        console.debug(`${this.title} - createAnnotationFeature`);

        const annotations = [];

        annotationsToBeCreated.forEach(annotationToBeCreated => {
            const annotationAttributes: any = {
                featureid: feature.attributes.id,
                guid: feature.attributes.guid,
                featuretype: featureType
            };

            if (featureType === 'road') {
                annotationAttributes.featuresubtype = annotationToBeCreated.attributes.featuresubtype;
            } else if (featureType === 'lot') {
                annotationAttributes.featuresubtype =
                    annotationToBeCreated.attributes.featuresubtype || (polygons && polygons.length ? polygons[0].attributes.type : null);
            } else if (featureType === 'interest') {
                annotationAttributes.featuresubtype =
                    annotationToBeCreated.attributes.featuresubtype ||
                    (polylines && polylines.length
                        ? polylines[0].attributes.type
                        : (annotationAttributes.featuresubtype = this.getRealValue(featureType, feature, 'type')));
            } else {
                annotationAttributes.featuresubtype = this.getRealValue(featureType, feature, 'type');
            }

            Object.entries(annotationToBeCreated.attributes).forEach(kv => (annotationAttributes[kv[0]] = kv[1]));

            const annotationGraphic = new this.esriModuleService.Graphic(annotationToBeCreated.geometry);
            annotationGraphic.attributes = annotationAttributes;

            annotations.push(annotationGraphic);
        });

        this.populateLabelExpression(annotations, polygons);
        const annotationsWithLabelExpression = annotations.filter(a => a.attributes.labelexpression);
        if (annotationsWithLabelExpression.length) {
            const annotationsToBeCreated = [...this.annotationsToBeCreated, ...annotationsWithLabelExpression].filter(a => a.geometry);
            this.annotationsToBeCreated.length = 0;

            if (annotationsToBeCreated.length) {
                this.createAnnotations(annotationsToBeCreated, false).subscribe();
            }
        }
    }

    private create(feature: any, featureType: string, polygons: any[], polylines: any[]) {
        console.debug(`${this.title} - create - feature type:`, featureType);

        // Use a copy of the feature to prevent unwanted feature modifications
        feature = this.spatialUtilService.copyGraphic(feature);

        let featureSubType = String(this.getRealValue(featureType, feature, featureType === 'constructPoint' ? 'marktype' : 'type'));

        const roadLegalParcelTypeLookup = this.getRoadLegalParcelTypeLookup();
        const roadWideningLegalParcelTypeLookup = this.getRoadWideningLegalParcelTypeLookup();

        const highestAreaPolygonId = this.getHighestAreaPolygonId(polygons);

        if ((!polygons || polygons.length === 0) && (!polylines || polylines.length === 0)) {
            this.createAnnotation(
                undefined,
                [roadLegalParcelTypeLookup],
                highestAreaPolygonId,
                undefined,
                feature,
                featureType,
                featureSubType,
                polygons,
                polylines
            );
        } else if (polygons) {
            polygons.forEach(polygon => {
                featureSubType = this.determineFeatureSubType(
                    feature,
                    polygon,
                    [roadLegalParcelTypeLookup],
                    [roadWideningLegalParcelTypeLookup],
                    featureType
                );

                if (featureType !== 'road' || feature.attributes.type === 11) {
                    this.createAnnotation(
                        polygon,
                        [roadLegalParcelTypeLookup],
                        polygon.attributes.id,
                        undefined,
                        feature,
                        featureType,
                        featureSubType,
                        polygons,
                        polylines
                    );
                } else {
                    this.createAnnotation(
                        polygon,
                        [roadLegalParcelTypeLookup],
                        highestAreaPolygonId,
                        undefined,
                        feature,
                        featureType,
                        featureSubType,
                        polygons,
                        polylines
                    );
                }
            });
        }

        if (polylines) {
            polylines.forEach(polyline => {
                this.createAnnotation(
                    undefined,
                    [roadLegalParcelTypeLookup],
                    highestAreaPolygonId,
                    polyline,
                    feature,
                    featureType,
                    featureSubType,
                    polygons,
                    polylines
                );
            });
        }
    }

    private createAnnotationObject(
        feature: any,
        annotationConfiguration: AnnotationConfigLookup,
        polygon: any,
        annotationsToBeCreated: any[],
        featureType: string,
        attribute10id: any,
        polyline: any
    ) {
        console.debug(`${this.title} - createAnnotationObject`, feature);

        const featureAttributeValue = feature.attributes[annotationConfiguration.featureAttribute];
        const translatedAttributeValue = this.translateAttributeValue(annotationConfiguration, featureAttributeValue, polygon);

        let annotationToBeCreated: any;

        if (annotationConfiguration.parent === null) {
            annotationToBeCreated = {id: annotationConfiguration.id};
            annotationToBeCreated.attributes = {visible: null};

            annotationToBeCreated.attributes[annotationConfiguration.annotationAttribute] =
                translatedAttributeValue != null ? translatedAttributeValue : annotationConfiguration.defaultForNullValue ? '' : null;
            // TODO DCX-9253: if we store id value instead of translated value (description or dispList) in attribute# in annotation table,
            //? feature.attributes[annotationConfiguration.featureAttribute] : (annotationConfiguration.defaultForNullValue ? "" : null);

            if (polyline) {
                this.setRotationValues(annotationToBeCreated, polyline);
            } else {
                this.setRotationValues(annotationToBeCreated, feature);
            }

            if (polygon !== undefined) {
                if (featureType !== 'road' || feature.attributes.type === 11) {
                    annotationToBeCreated.attributes.childtype = 'polygon';
                    annotationToBeCreated.attributes.childid = Number(polygon.attributes.id);
                }

                if ((featureType === 'road' || featureType === 'lot') && [10, 11].includes(feature.attributes.type)) {
                    annotationToBeCreated.attributes.attribute9 = annotationToBeCreated.attributes.childtype;
                    if (!annotationToBeCreated.attributes.attribute10) {
                        annotationToBeCreated.attributes.attribute10 = attribute10id || this.annotationUtilityService.newAnnotationSetGuid();
                    }
                }
            }

            if (polyline !== undefined) {
                annotationToBeCreated.attributes.childtype = 'polyline';
                annotationToBeCreated.attributes.childid = Number(polyline.attributes.id);
            }

            annotationsToBeCreated.push(annotationToBeCreated);
        } else {
            annotationToBeCreated = annotationsToBeCreated.find(
                e =>
                    e.id === annotationConfiguration.parent &&
                    (!e.attributes.childid ||
                        (polygon && e.attributes.childid === Number(polygon.attributes.id) && e.attributes.childtype === 'polygon') ||
                        (polyline && e.attributes.childid === Number(polyline.attributes.id) && e.attributes.childtype === 'polyline'))
            );

            if (annotationToBeCreated) {
                annotationToBeCreated.attributes[annotationConfiguration.annotationAttribute] =
                    translatedAttributeValue != null ? translatedAttributeValue : annotationConfiguration.defaultForNullValue ? '' : null;
                // TODO DCX-9253: if we store id value instead of translated value (description or dispList) in attribute# in annotation table,
                //? feature.attributes[annotationConfiguration.featureAttribute] : (annotationConfiguration.defaultForNullValue ? "" : null);
            }
        }

        if (annotationToBeCreated) {
            // set the configuration for use calculating geometry
            annotationToBeCreated.configuration = annotationConfiguration;
        }
    }

    /**
     * Get description or display label values by id from lookup stated in lookupService in AnnotationConfigLookup
     * @param annotationConfigLookup AnnotationConfigLookup
     * @param featureAttributeValue id from point, line, constructPoint, road, or lot feature for lookup service
     * @param polygon
     */
    private translateAttributeValue(annotationConfigLookup: AnnotationConfigLookup, featureAttributeValue: any, polygon: any) {
        console.debug(`${this.title} - translateAttributeValue - featureAttributeValue = `, featureAttributeValue);

        if (annotationConfigLookup.featureAttribute === 'polygon.statedarea' && polygon) {
            return polygon.attributes.statedarea;
        } else if (featureAttributeValue && annotationConfigLookup.lookupService !== null) {
            const output = featureAttributeValue;
            const lookups = this.lookupService.getLookups(annotationConfigLookup.lookupService);

            if (featureAttributeValue.length > 2 && featureAttributeValue.indexOf('[') !== -1 && featureAttributeValue.indexOf(']') !== -1) {
                featureAttributeValue = featureAttributeValue.replace(/[[\]]/g, '');
            }

            const lookup = lookups.find(
                (l: any) => l.id === Number(featureAttributeValue) || l.id === featureAttributeValue || l.description === featureAttributeValue
            );

            if (lookup) {
                // use the display label if it exists, otherwise use the description
                return lookup.displayLabel || lookup.description;
            } else {
                return this.removeLookupIdBasedOnCustomValue(output);
            }
        } else {
            return featureAttributeValue;
        }
    }

    private removeLookupIdBasedOnCustomValue(attributeValue: string) {
        console.debug(`${this.title} - removeLookupIdBasedOnCustomValue`);

        let customValue = attributeValue;

        if (attributeValue.length > 2 && attributeValue.includes('[') && attributeValue.includes(']') && attributeValue.includes('-')) {
            customValue = attributeValue.substring(0, attributeValue.indexOf('-') + 1);
            customValue = attributeValue.replace(customValue, '');
        }

        return customValue;
    }

    private labelPoints() {
        const geometryService = this.spatialUtilService.getGeometryService();
        const geometries = this.labelPointsTimeoutInfoList.map(v => v.geometry);
        const callbacks = this.labelPointsTimeoutInfoList.map(v => v.callback);
        this.labelPointsTimeoutInfoList.length = 0;

        geometryService.labelPoints(geometries, (labelPoints: any[]) => callbacks.forEach((callback, i) => callback(labelPoints[i])));
    }

    getPolygonCentroid(feature: any, callback: any) {
        console.debug(`${this.title} - getPolygonCentroid`);

        if (this.labelPointsTimeout) {
            window.clearTimeout(this.labelPointsTimeout);
        }
        this.labelPointsTimeoutInfoList.push({geometry: feature.geometry, callback: callback});
        this.labelPointsTimeout = window.setTimeout(() => this.labelPoints(), 500);
    }

    private calculateOffsetX(polygon: any, annotationConfigurations: AnnotationConfigLookup[]) {
        let offsetX = 0;

        if (polygon.attributes && polygon.attributes.interestids) {
            const annotationConfiguration = annotationConfigurations.find(ac => ac.annotationAttribute === 'attribute2');

            if (annotationConfiguration && annotationConfiguration.singleAnnoOffsetDistance) {
                offsetX = 2 * this.printAreaService.getReferenceScaleDivisor() * annotationConfiguration.singleAnnoOffsetDistance;
            }
        }

        return offsetX;
    }

    private setRotationValues(annotation: any, feature: any) {
        console.debug(`${this.title} - setRotation`);

        let rotationDms = '90:00:00';

        if (feature.geometry && (feature.geometry.type === 'line' || feature.geometry.type === 'polyline')) {
            let bearing = this.angleUtilService.getLineBearing(feature);
            bearing = bearing < 180 ? bearing + 180 : bearing - 180;
            rotationDms = this.angleUtilService.ddToDms(bearing);
        } else if (feature.attributes.rotationdms) {
            rotationDms = feature.attributes.rotationdms;
        } else if (annotation.attributes.rotationdms) {
            rotationDms = annotation.attributes.rotationdms;
        }

        annotation.attributes.rotationdms = rotationDms;
        annotation.attributes.rotation = this.angleUtilService.dmsToDd(rotationDms);
    }

    private offsetGeometry(offsetDistance: number, annotation: any) {
        const radians = ((annotation.attributes.rotation - 90) * Math.PI) / 180;
        const offsetX = offsetDistance * Math.sin(radians);
        const offsetY = offsetDistance * Math.cos(radians);

        annotation.attributes.x = annotation.geometry.x = parseFloat(annotation.geometry.x) + offsetX;
        annotation.attributes.y = annotation.geometry.y = parseFloat(annotation.geometry.y) + offsetY;
    }

    private getLegalParcelTypeLookups(): LegalParcelTypeLookup[] {
        return this.lookupService.getLookups('legalParcelType');
    }

    private getRoadLegalParcelTypeLookup() {
        return this.getLegalParcelTypeLookups().find(l => l.description === 'Road');
    }

    private getRoadWideningLegalParcelTypeLookup() {
        return this.getLegalParcelTypeLookups().find(l => l.description === 'Road Widening');
    }

    private getHighestAreaPolygonId(polygons: any[]) {
        if (!polygons?.length) {
            return 0;
        }

        const roadLegalParcelTypeLookup = this.getRoadLegalParcelTypeLookup();

        const highestAreaPolygonId = polygons
            .filter(p => p.attributes.type === roadLegalParcelTypeLookup.id && p.attributes.statedarea)
            .sort((a, b) => Number(b.attributes.statedarea) - Number(a.attributes.statedarea))
            .map(p => p.attributes.id)[0];

        return highestAreaPolygonId || 0;
    }

    private determineFeatureSubType(feature: any, polygon: any, roadTypes: any[], roadWideningTypes: any[], featureType: string) {
        let featureSubType = String(this.getRealValue(featureType, feature, 'type'));

        if (polygon && (featureType === 'road' || featureType === 'lot')) {
            roadTypes.forEach(roadType => {
                if (polygon.attributes.type === roadType.id) {
                    featureSubType = polygon.attributes.type;
                }
            });

            roadWideningTypes.forEach(roadWideningType => {
                if (polygon.attributes.type === roadWideningType.id) {
                    featureSubType = polygon.attributes.type;
                }
            });
        }

        return featureSubType;
    }

    private getRealValue(featureType: string, featureViewModel: any, attribute: any) {
        console.debug(`${this.title} : getRealValue for attribute: ${attribute}`);

        const feature = this.spatialUtilService.findById(featureType, featureViewModel.attributes.id);

        const displayValue = featureViewModel.attributes[attribute];
        const realValue = feature && feature.attributes[attribute];

        if (realValue && isNaN(this.filterFloat(displayValue)) && !isFinite(displayValue)) {
            return realValue;
        }

        return displayValue;
    }

    private filterFloat(value: string) {
        return /^([-+])?([0-9]+(\.[0-9]+)?|Infinity)$/.test(value) ? Number(value) : NaN;
    }

    private createAnnotationsAfterTimeout() {
        if (this.createTimeout) {
            window.clearTimeout(this.createTimeout);
        }

        this.createTimeout = window.setTimeout(() => {
            if (this.annotationsToBeCreated.length) {
                const annotationsToBeCreated = this.annotationsToBeCreated.filter(a => a.geometry);
                this.annotationsToBeCreated = [];

                if (annotationsToBeCreated.length) {
                    this.createAnnotations(annotationsToBeCreated, false).subscribe();
                }
            }
        }, 200);
    }

    private updateAnnotationsAfterTimeout() {
        if (this.updateTimeout) {
            window.clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = window.setTimeout(() => {
            const annotationIds = this.spatialUtilService.getAnnotations().map((a: any) => a.attributes.id);

            if (this.annotationsToBeUpdated.length) {
                const annotationsToBeUpdated = this.annotationsToBeUpdated.filter(a => annotationIds.includes(a.attributes.id));
                this.annotationsToBeUpdated.length = 0;

                if (annotationsToBeUpdated.length) {
                    this.commitAnnotationsUpdate(annotationsToBeUpdated);
                }
            }
        }, 200);
    }

    private delete(featureLayer: any, features: any[], parentFeatureType: string, parentFeatureId: number) {
        const featureLayerType = this.spatialUtilService.getFeatureLayerName(featureLayer);

        console.debug(`${this.title} : delete - featureLayerType: ${featureLayerType}`);

        const annotationsToDelete = this.spatialUtilService.getAnnotations().filter((annotation: any) => {
            const attrs = annotation.attributes;

            if (attrs.featuretype === parentFeatureType && parentFeatureId && attrs.featureid === parentFeatureId && attrs.attribute1) {
                if (
                    (parentFeatureType === 'interest' &&
                        (!attrs.childtype || attrs.childtype === 'polygon') &&
                        !this.spatialUtilService.getInterests().some((i: any) => i.attributes.id === parentFeatureId && i.attributes.polygonids)) ||
                    (parentFeatureType === 'road' &&
                        !this.spatialUtilService.getRoads().some((r: any) => r.attributes.id === parentFeatureId && r.attributes.polygonids))
                ) {
                    // All parent feature associations removed
                    return true;
                }
            }

            return features.some(feature => {
                const featureAttrs = feature.attributes;
                if (parentFeatureType) {
                    return (
                        attrs.featuretype === parentFeatureType &&
                        (!parentFeatureId || attrs.featureid === parentFeatureId) &&
                        (featureAttrs.attribute9 && featureAttrs.attribute10
                            ? attrs.attribute9 === featureAttrs.attribute9 && attrs.attribute10 === featureAttrs.attribute10
                            : attrs.childtype === featureLayerType && attrs.childid.toString() === featureAttrs.id.toString())
                    );
                }

                return attrs.featuretype === featureLayerType && attrs.featureid === featureAttrs.id;
            });
        });

        this.deleteAnnotations(annotationsToDelete).subscribe();
    }

    private commitAnnotationsUpdate(annotations: any) {
        const logPrefix = `${this.title} - commitAnnotationsUpdate`;
        console.debug(logPrefix);

        return this.spatialUtilService.applyEdits('annotation', null, annotations, null).pipe(
            map(res => {
                console.debug(`${logPrefix} - updates:`, res.updates);

                const u0 = res.updates[0];
                if (u0.success) {
                    console.debug(`${logPrefix} - annotations updated successfully`);

                    this.spatialUtilService.updateMapGraphicGeometries(this.spatialUtilService.getAnnotationMapLayer(), annotations);
                    this.eventBus.cast(this.designProperties.EVENTS.GEOMETRY_FEATURES_UPDATED, {
                        features: annotations,
                        featureLayer: this.spatialUtilService.getAnnotationMapLayer()
                    });

                    return true;
                } else if (u0.error?.message && u0.error?.code !== 1011) {
                    throw new Error(u0.error?.message);
                } else {
                    return false;
                }
            }),
            catchError(err => {
                let errorMessage = 'Error updating annotation features.';
                if (err) {
                    errorMessage = `${errorMessage} Error : ${err}`;
                }
                console.error(`${logPrefix}`, errorMessage);
                this.alertModalService.createAlertMessage(errorMessage, 'Ok');
                return err;
            })
        );
    }

    update(feature: any, featureType: string, options: {polygons?: any[]; childPolygon?: any; updateGeometry?: boolean; createAnnotation?: boolean}) {
        console.debug(`${this.title} - update`);

        // Use a copy of the feature to prevent unwanted feature modifications
        feature = this.spatialUtilService.copyGraphic(feature);

        const polygons = options.polygons;
        const childPolygon = options.childPolygon;
        const updateGeometry = !!options.updateGeometry;
        const createAnnotation = !!options.createAnnotation;

        const annotations = this.spatialUtilService
            .getAnnotations()
            .filter((f: any) => f.attributes.featuretype === featureType && f.attributes.featureid === feature.attributes.id)
            .map((f: any) => new this.esriModuleService.Graphic(f.toJson()));

        if (annotations.length === 0) {
            if (createAnnotation) {
                this.eventBus.cast('createAnnotation', {feature: feature, featureType: featureType});
            }
            return of(null);
        }

        annotations.forEach((annotation: any) => {
            if (featureType === 'road' || featureType === 'lot') {
                const attrs = annotation.attributes;
                Object.keys(attrs)
                    .filter(
                        k =>
                            k.includes('attribute') &&
                            (attrs[k] === undefined || attrs[k] === null) &&
                            (!attrs.childtype || (attrs.childtype === 'polygon' && [10, 11].includes(attrs.featuresubtype)))
                    )
                    .forEach(k => delete attrs[k]);
            }

            if (featureType === 'line') {
                const attrs = annotation.attributes;
                Object.keys(attrs)
                    .filter(k => k.includes('attribute') && (attrs[k] === undefined || attrs[k] === null))
                    .forEach(k => delete attrs[k]);
            }

            this.getAnnotationConfigurations(annotation.attributes.featuretype, annotation.attributes.featuresubtype)
                .filter(ac => Object.hasOwn(annotation.attributes, ac.annotationAttribute) || ac.parent)
                .forEach(ac => {
                    const matchingPolygons = (polygons || []).filter(
                        p => Number(p.attributes.id) === Number(annotation.attributes.childid) && annotation.attributes.childtype === 'polygon'
                    );

                    this.updateAnnotationObject(annotation, feature, ac, matchingPolygons[0] || childPolygon, featureType);

                    matchingPolygons.forEach(polygon => {
                        if (featureType === 'road') {
                            feature.attributes.type = polygon.attributes.type;
                        }
                        this.updateAnnotationObject(annotation, feature, ac, polygon, featureType);
                    });
                });
        });

        const observable = updateGeometry ? this.setAnnotationsGeometry(feature, featureType, polygons, undefined, annotations) : of(null);

        return observable.pipe(
            switchMap(() => {
                const annotationsWithLabelExpression = annotations.filter((a: any) => a.attributes.labelexpression);
                if (annotationsWithLabelExpression.length) {
                    const allAnnotationIds = this.spatialUtilService.getAnnotations().map((a: any) => a.attributes.id);

                    const annotationsToBeUpdated = [...this.annotationsToBeUpdated, ...annotationsWithLabelExpression].filter(a =>
                        allAnnotationIds.includes(a.attributes.id)
                    );
                    this.annotationsToBeUpdated.length = 0;

                    if (annotationsToBeUpdated.length) {
                        this.populateLabelExpression(annotationsToBeUpdated, undefined);
                        return this.commitAnnotationsUpdate(annotationsToBeUpdated);
                    }
                }

                const annotationsWithoutLabelExpression = annotations.filter(a => a.attributes.id && !a.attributes.labelexpression);
                if (annotationsWithoutLabelExpression.length) {
                    return this.deleteAnnotations(annotationsWithoutLabelExpression);
                }

                return of(null);
            }),
            catchError(err => this.alertModalService.createAlertMessage('Error updating annotations: ' + err, 'Ok'))
        );
    }

    private updateAnnotationObject(
        annotation: any,
        feature: any,
        annotationConfiguration: AnnotationConfigLookup,
        polygon: any,
        featureType: string
    ) {
        console.debug(`${this.title} - updateAnnotationObject`, annotation, feature, annotationConfiguration, polygon, featureType);

        // set the configuration for use calculating geometry
        annotation.configuration = annotationConfiguration;

        const featureAttributeValue = feature.attributes[annotationConfiguration.featureAttribute];
        const translatedAttributeValue = this.translateAttributeValue(annotationConfiguration, featureAttributeValue, polygon);
        switch (featureType) {
            case 'road':
            case 'lot':
                if (Object.hasOwn(feature.attributes, annotationConfiguration.featureAttribute)) {
                    if (Object.hasOwn(annotation.attributes, 'childid')) {
                        // the annotation is part of an annotation set - only update attributes that already exist
                        if (![null, undefined].includes(annotation.attributes[annotationConfiguration.annotationAttribute])) {
                            annotation.attributes[annotationConfiguration.annotationAttribute] =
                                translatedAttributeValue != null ? translatedAttributeValue : annotationConfiguration.defaultForNullValue ? '' : null;
                            // TODO DCX-9253: store id value instead of translated value (description or dispList) in attribute# in annotation table,
                            //? feature.attributes[ac.featureAttribute] : ac.defaultForNullValue ? '' : null

                            if (feature.attributes.rotationdms || feature.attributes.bearing) {
                                this.setRotationValues(annotation, feature);
                            }
                        }
                    } else {
                        // single annotation - always update the attribute
                        // TODO DCX-9253: store id value instead of translated value (description or dispList) in attribute# in annotation table,
                        // annotation.attributes[ac.annotationAttribute] = feature.attributes[ac.featureAttribute];
                        annotation.attributes[annotationConfiguration.annotationAttribute] =
                            translatedAttributeValue != null ? translatedAttributeValue : annotationConfiguration.defaultForNullValue ? '' : null;
                        this.setRotationValues(annotation, feature);
                    }
                }
                break;
            default:
                // TODO DCX-9253: if we store id value instead of translated value (description or dispList) in attribute# in annotation table,
                // annotation.attributes[ac.annotationAttribute] = feature.attributes[ac.featureAttribute];
                annotation.attributes[annotationConfiguration.annotationAttribute] =
                    translatedAttributeValue != null ? translatedAttributeValue : annotationConfiguration.defaultForNullValue ? '' : null;
                this.setRotationValues(annotation, feature);
                break;
        }
    }

    private flipLineAnnotation(annotation: any, x0: number, y0: number, x1: number, y1: number) {
        // find the point on the line where a line from the annotation would meet at 90deg

        const lineRadians = Math.atan2(y1 - y0, x1 - x0);
        const featureRadians = Math.atan2(annotation.geometry.y - y0, annotation.geometry.x - x0);
        const hypotenuseDistance = Math.sqrt(Math.pow(annotation.geometry.x - x0, 2) + Math.pow(annotation.geometry.y - y0, 2));
        const angle = Math.atan2(Math.sin(featureRadians - lineRadians), Math.cos(featureRadians - lineRadians));
        const intersectDistance = hypotenuseDistance * Math.cos(angle);
        const intersectX = x0 + intersectDistance * Math.cos(lineRadians);
        const intersectY = y0 + intersectDistance * Math.sin(lineRadians);
        const flippedRadians = Math.atan2(annotation.geometry.y - intersectY, annotation.geometry.x - intersectX) + Math.PI;
        const distanceFromLine = hypotenuseDistance * Math.sin(angle);

        // scale the distance from the line intersect relative to the change in map scale
        const updatedX = intersectX + distanceFromLine * Math.cos(flippedRadians) * (angle < 0 ? -1 : 1);
        const updatedY = intersectY + distanceFromLine * Math.sin(flippedRadians) * (angle < 0 ? -1 : 1);
        if (!isNaN(updatedX) && !isNaN(updatedY) && (annotation.geometry.x !== updatedX || annotation.geometry.y !== updatedY)) {
            annotation.geometry.x = updatedX;
            annotation.geometry.y = updatedY;
            annotation.attributes.x = annotation.geometry.x;
            annotation.attributes.y = annotation.geometry.y;
            return annotation;
        }
    }

    private updateFeatures(featureLayer: any, features: any[]) {
        return this.featuresApplyUpdateService.updateFeaturesByType(featureLayer, features);
    }

    private reversePolylines(polylines: any[]) {
        polylines.forEach(polyline => {
            if (polyline.geometry) {
                polyline.geometry.paths.reverse();
                polyline.geometry.paths.forEach(function (path) {
                    path.reverse();
                });
            }

            if (polyline.attributes.pointids) {
                polyline.attributes.pointids = polyline.attributes.pointids.split(',').reverse().join();
            }
        });
    }
}
